import Phaser from 'phaser';

export type EnemyState = 'marching' | 'engaging' | 'dead';

export interface EnemyConfig {
  x: number;
  y: number;
  type: string;
  hp: number;
  atk: number;
  speedPxPerSec: number;
  attackIntervalMs: number;
  contactX: number; // x a partir de la cual el enemigo se detiene y pelea
  textureKey: string;
}

const HP_BAR_WIDTH = 22;

/**
 * Enemigo: marcha de derecha a izquierda hasta `contactX`, ahí se detiene y
 * ataca en melee cada `attackIntervalMs`. Barra de HP roja + número sobre
 * la cabeza, como pide la spec.
 */
export class Enemy {
  readonly view: Phaser.GameObjects.Container;
  readonly type: string;
  readonly atk: number;
  readonly maxHp: number;
  hp: number;
  state: EnemyState = 'marching';

  private readonly sprite: Phaser.GameObjects.Image;
  private readonly hpBarFill: Phaser.GameObjects.Rectangle;
  private readonly hpText: Phaser.GameObjects.Text;
  private readonly speedPxPerSec: number;
  private readonly attackIntervalMs: number;
  private readonly contactX: number;
  private attackCooldownMs: number;

  constructor(
    private readonly scene: Phaser.Scene,
    parent: Phaser.GameObjects.Container,
    config: EnemyConfig
  ) {
    this.type = config.type;
    this.atk = config.atk;
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.speedPxPerSec = config.speedPxPerSec;
    this.attackIntervalMs = config.attackIntervalMs;
    this.attackCooldownMs = config.attackIntervalMs;
    this.contactX = config.contactX;

    this.view = scene.add.container(config.x, config.y);
    this.sprite = scene.add.image(0, 0, config.textureKey).setOrigin(0.5, 1);
    const hpBarBg = scene.add.rectangle(0, -20, HP_BAR_WIDTH, 4, 0x1b1030).setOrigin(0.5);
    this.hpBarFill = scene.add
      .rectangle(-HP_BAR_WIDTH / 2, -20, HP_BAR_WIDTH, 2.4, 0xe0524d)
      .setOrigin(0, 0.5);
    this.hpText = scene.add
      .text(0, -29, `${this.hp}`, { fontFamily: 'monospace', fontSize: '9px', color: '#ffffff' })
      .setOrigin(0.5);
    this.view.add([hpBarBg, this.hpBarFill, this.sprite, this.hpText]);
    parent.add(this.view);
  }

  get x(): number {
    return this.view.x;
  }

  get y(): number {
    return this.view.y;
  }

  get isDead(): boolean {
    return this.hp <= 0;
  }

  get isEngaging(): boolean {
    return this.state === 'engaging';
  }

  /** Avanza marcha o ataque; devuelve true si toca golpear al héroe este frame. */
  update(deltaMs: number): boolean {
    if (this.state === 'dead') return false;

    if (this.state === 'marching') {
      const dx = (this.speedPxPerSec * deltaMs) / 1000;
      this.view.x -= dx;
      if (this.view.x <= this.contactX) {
        this.view.x = this.contactX;
        this.state = 'engaging';
        this.attackCooldownMs = this.attackIntervalMs;
      }
      return false;
    }

    // engaging: cuenta regresiva hasta el próximo golpe melee
    this.attackCooldownMs -= deltaMs;
    if (this.attackCooldownMs <= 0) {
      this.attackCooldownMs += this.attackIntervalMs;
      return true;
    }
    return false;
  }

  /** Aplica daño ya resuelto (crítico/etc. se calculan antes de llamar). */
  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - amount);
    this.hpBarFill.width = HP_BAR_WIDTH * Phaser.Math.Clamp(this.hp / this.maxHp, 0, 1);
    this.hpText.setText(`${this.hp}`);
    if (this.hp <= 0) {
      this.state = 'dead';
    }
  }

  /** Destello blanco breve al recibir un golpe, para dar feedback visual. */
  playHitFlash(): void {
    this.sprite.setTintFill(0xffffff);
    this.scene.time.delayedCall(80, () => this.sprite.clearTint());
  }

  destroy(): void {
    this.view.destroy();
  }
}
